import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { ChevronDown, ChevronUp, ExternalLink, Eye, Loader2 } from "lucide-react";
import { mutate } from "swr";
import { Button } from "~/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "~/components/ui/collapsible";
import { Switch } from "~/components/ui/switch";
import useToast from "~/components/ui/toast";
import { useFetcher } from "~/hooks/useFetcher";
import { TabHeader } from "~/pages/manifold-nextgen/components/TabHeader";
import { ToolListDialog } from "~/pages/manifold-nextgen/components/agents/ToolListDialog";
import { useCanEditAgent } from "~/pages/manifold-nextgen/hooks/useCanEditAgent";
import {
  Agent,
  AgentConnector,
  AgentScope,
  AgentToolkit,
  Connector,
  Runtime,
  Toolkit,
  agentConnectorsPath,
  agentToolkitsPath,
  connectorToolsPath,
  connectorsPath,
  disableAgentConnector,
  disableAgentToolkit,
  enableAgentConnector,
  enableAgentToolkit,
  runtimesPath,
  toolkitToolsPath,
  toolkitsPath,
} from "~/services/agents";

// Mirrors the backend `ToolkitId.CODE_INTERPRETER`. Runtimes only take effect
// when this toolkit is mounted on the agent, so they're nested under its row
// here rather than shown as an unconditional, separate list.
const CODE_INTERPRETER_TOOLKIT_ID = "code_interpreter";

interface SectionProps {
  agent: Agent;
  canEdit: boolean;
}

interface CapabilitySectionHeaderProps {
  title: string;
  count?: number;
  max?: number;
  canEdit: boolean;
  description: string;
}

// A sub-section heading within Capabilities — sized to match the Behavior
// tab's field labels rather than TabHeader's full-page title.
const CapabilitySectionHeader = ({
  title,
  count,
  max,
  canEdit,
  description,
}: CapabilitySectionHeaderProps) => {
  const showCounter = count !== undefined && max !== undefined;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline gap-2">
        <h3 className="text-base font-medium text-foreground">{title}</h3>
        {showCounter && (
          <span className="text-xs text-muted-foreground">
            {canEdit ? (
              <>
                {count} of {max} enabled
              </>
            ) : (
              <>{count} enabled</>
            )}
          </span>
        )}
      </div>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  );
};

const RuntimesList = ({
  runtimes,
  isLoading,
}: {
  runtimes: Runtime[] | undefined;
  isLoading: boolean;
}) => {
  const [open, setOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 px-4 py-3">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="h-8 animate-pulse rounded-md bg-muted" />
        ))}
      </div>
    );
  }

  if (!runtimes?.length) return null;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex w-full cursor-pointer items-center gap-1.5 rounded-md px-4 pb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground hover:text-foreground">
        <span>Runtimes ({runtimes.length})</span>
        {open ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
      </CollapsibleTrigger>
      <CollapsibleContent className="ml-8 mb-3 flex flex-col gap-2 pr-4">
        {runtimes.map((runtime) => (
          <div key={runtime.id}>
            <p className="text-sm font-medium">{runtime.name}</p>
            {runtime.description && (
              <p className="text-xs text-muted-foreground">{runtime.description}</p>
            )}
          </div>
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
};

const ToolkitsSection = ({ agent, canEdit }: SectionProps) => {
  const { showError } = useToast();
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());
  const [viewingToolkit, setViewingToolkit] = useState<Toolkit | null>(null);

  const { data: toolkits, isLoading: toolkitsLoading } = useFetcher<Toolkit[]>(toolkitsPath());
  const { data: agentToolkits, isLoading: agentToolkitsLoading } = useFetcher<AgentToolkit[]>(
    agentToolkitsPath(agent.id)
  );
  const { data: runtimes, isLoading: runtimesLoading } = useFetcher<Runtime[]>(runtimesPath());

  const enabledIds = new Set((agentToolkits ?? []).map((t) => t.toolkit.id));
  const isLoading = toolkitsLoading || agentToolkitsLoading;
  const totalCount = toolkits?.length ?? 0;
  const displayedToolkits = canEdit
    ? (toolkits ?? [])
    : (toolkits ?? []).filter((t) => enabledIds.has(t.id));

  const handleToggle = async (toolkit: Toolkit, enabled: boolean) => {
    setTogglingIds((prev) => new Set(prev).add(toolkit.id));
    try {
      if (enabled) {
        await enableAgentToolkit(agent.id, toolkit.id);
      } else {
        await disableAgentToolkit(agent.id, toolkit.id);
      }
      await mutate(agentToolkitsPath(agent.id));
    } catch {
      showError(`Failed to ${enabled ? "enable" : "disable"} "${toolkit.display_name}" Toolkit`);
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(toolkit.id);
        return next;
      });
    }
  };

  return (
    <section className="flex flex-col gap-4">
      <CapabilitySectionHeader
        title="Toolkits"
        count={isLoading || totalCount === 0 ? undefined : enabledIds.size}
        max={isLoading || totalCount === 0 ? undefined : totalCount}
        canEdit={canEdit}
        description="Toolkits expose tools the agent can call during a conversation. Mount the ones necessary for this agent to do its job."
      />

      {isLoading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-14 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      ) : !displayedToolkits.length ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          {canEdit ? "No toolkits available" : "No toolkits enabled"}
        </p>
      ) : (
        <div className="flex flex-col divide-y divide-border rounded-lg border border-border">
          {displayedToolkits.map((toolkit) => {
            const isCodeInterpreter = toolkit.id === CODE_INTERPRETER_TOOLKIT_ID;
            return (
              <div key={toolkit.id} className="flex flex-col">
                <div className="flex items-center justify-between gap-4 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{toolkit.display_name}</p>
                    {toolkit.description && (
                      <p className="text-xs text-muted-foreground">{toolkit.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setViewingToolkit(toolkit)}
                      aria-label={`View tools in ${toolkit.display_name}`}
                      title="View tools"
                      className="size-8 text-muted-foreground"
                    >
                      <Eye className="size-4" />
                    </Button>
                    {canEdit &&
                      (togglingIds.has(toolkit.id) ? (
                        <div className="flex h-[1.15rem] w-8 items-center justify-center">
                          <Loader2 className="size-3 animate-spin text-muted-foreground" />
                        </div>
                      ) : (
                        <Switch
                          checked={enabledIds.has(toolkit.id)}
                          onCheckedChange={(checked) => handleToggle(toolkit, checked)}
                          aria-label={`${enabledIds.has(toolkit.id) ? "Disable" : "Enable"} ${toolkit.display_name}`}
                        />
                      ))}
                  </div>
                </div>
                {isCodeInterpreter && (canEdit || enabledIds.has(toolkit.id)) && (
                  <RuntimesList runtimes={runtimes} isLoading={runtimesLoading} />
                )}
              </div>
            );
          })}
        </div>
      )}

      <ToolListDialog
        open={!!viewingToolkit}
        onOpenChange={(open) => !open && setViewingToolkit(null)}
        title={viewingToolkit?.display_name ?? ""}
        description={viewingToolkit?.description}
        fetchPath={viewingToolkit ? toolkitToolsPath(viewingToolkit.id) : null}
      />
    </section>
  );
};

const ConnectorsSection = ({ agent, canEdit }: SectionProps) => {
  const { showError } = useToast();
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());
  const [viewingConnector, setViewingConnector] = useState<Connector | null>(null);

  const { data: connectors, isLoading: connectorsLoading } =
    useFetcher<Connector[]>(connectorsPath());
  const { data: agentConnectors, isLoading: agentConnectorsLoading } = useFetcher<AgentConnector[]>(
    agentConnectorsPath(agent.id)
  );

  const enabledIds = new Set((agentConnectors ?? []).map((c) => c.connector.id));
  const isLoading = connectorsLoading || agentConnectorsLoading;
  const totalCount = connectors?.length ?? 0;
  const displayedConnectors = canEdit
    ? (connectors ?? [])
    : (connectors ?? []).filter((c) => enabledIds.has(c.id));

  const handleToggle = async (connector: Connector, enabled: boolean) => {
    const label = connector.display_name ?? connector.name;
    setTogglingIds((prev) => new Set(prev).add(connector.id));
    try {
      if (enabled) {
        await enableAgentConnector(agent.id, connector.id);
      } else {
        await disableAgentConnector(agent.id, connector.id);
      }
      await mutate(agentConnectorsPath(agent.id));
    } catch {
      showError(`Failed to ${enabled ? "enable" : "disable"} "${label}" Connector`);
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(connector.id);
        return next;
      });
    }
  };

  return (
    <section className="flex flex-col gap-4">
      <CapabilitySectionHeader
        title="Connectors"
        count={isLoading || totalCount === 0 ? undefined : enabledIds.size}
        max={isLoading || totalCount === 0 ? undefined : totalCount}
        canEdit={canEdit}
        description="Connectors expose tools hosted on external MCP servers. Attach the ones this agent needs to do its job."
      />

      {isLoading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-14 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      ) : !displayedConnectors.length ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          {canEdit ? "No connectors available" : "No connectors enabled"}
        </p>
      ) : (
        <div className="flex flex-col divide-y divide-border rounded-lg border border-border">
          {displayedConnectors.map((connector) => {
            const label = connector.display_name ?? connector.name;
            return (
              <div key={connector.id} className="flex items-center justify-between gap-4 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{label}</p>
                  {(connector.description || connector.docs_url) && (
                    <p className="text-xs text-muted-foreground">
                      {connector.description}
                      {connector.docs_url && (
                        <>
                          {" "}
                          <a
                            href={connector.docs_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-0.5 underline hover:text-foreground"
                          >
                            Learn more
                            <ExternalLink className="size-3" />
                          </a>
                        </>
                      )}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setViewingConnector(connector)}
                    aria-label={`View tools in ${label}`}
                    title="View tools"
                    className="size-8 text-muted-foreground"
                  >
                    <Eye className="size-4" />
                  </Button>
                  {canEdit &&
                    (togglingIds.has(connector.id) ? (
                      <div className="flex h-[1.15rem] w-8 items-center justify-center">
                        <Loader2 className="size-3 animate-spin text-muted-foreground" />
                      </div>
                    ) : (
                      <Switch
                        checked={enabledIds.has(connector.id)}
                        onCheckedChange={(checked) => handleToggle(connector, checked)}
                        aria-label={`${enabledIds.has(connector.id) ? "Disable" : "Enable"} ${label}`}
                      />
                    ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ToolListDialog
        open={!!viewingConnector}
        onOpenChange={(open) => !open && setViewingConnector(null)}
        title={viewingConnector?.display_name ?? viewingConnector?.name ?? ""}
        description={viewingConnector?.description}
        fetchPath={viewingConnector ? connectorToolsPath(viewingConnector.id) : null}
      />
    </section>
  );
};

interface AgentCapabilitiesTabProps {
  // Overrides the outlet-context agent — used when this tab is rendered
  // outside the AgentDetail route tree (the project context sidebar).
  agent?: Agent;
  // True when this tab is rendered inside the session context sidebar,
  // which only ever shows a read-only view (no edit affordances, no tab
  // title — the entity name is already shown in the sidebar header).
  sessionSidebarView?: boolean;
}

export const AgentCapabilitiesTab = ({
  agent: agentProp,
  sessionSidebarView = false,
}: AgentCapabilitiesTabProps) => {
  const outletAgent = useOutletContext<Agent>();
  const agent = agentProp ?? outletAgent;
  const canEditGrant = useCanEditAgent(agent);
  const canEdit = !sessionSidebarView && canEditGrant;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4">
      <TabHeader
        title={sessionSidebarView ? undefined : "Capabilities"}
        showReadOnlyBadge={!sessionSidebarView && agent.scope === AgentScope.SYSTEM}
      />

      <ToolkitsSection agent={agent} canEdit={canEdit} />
      <ConnectorsSection agent={agent} canEdit={canEdit} />
    </div>
  );
};
